import type { NodeTreeElement } from "./types.ts";

interface Options<TEntry extends NodeTreeElement<any>> {
  filterChildren?: (
    node: TEntry,
    ancestors: readonly TEntry[],
    slotName: string | undefined,
  ) => Array<keyof NonNullable<TEntry["childNodes"]>> | undefined;
  excludeRoot?: boolean;
}

export const traverseNodeSubtree = <TEntry extends NodeTreeElement<any>>(
  startNode: TEntry,
  onNode: (node: TEntry, ancestors: readonly TEntry[], slotName?: string) => void,
  { excludeRoot, filterChildren }: Options<TEntry> = {},
) => {
  const traverse = (node: TEntry, ancestors: readonly TEntry[], slotName: string | undefined) => {
    if (!(excludeRoot && node.id === startNode.id)) {
      onNode(node, ancestors, slotName);
    }
    if (!(node.childNodes || node.placeholder)) return;

    const selectedChildNames = filterChildren?.(node, ancestors, slotName);
    const childAncestors = [node, ...ancestors];
    if (node.childNodes) {
      for (const [name, valueOrValues] of Object.entries(node.childNodes)) {
        if (selectedChildNames && (selectedChildNames.length === 0 || !selectedChildNames.includes(name))) {
          continue;
        }
        if (Array.isArray(valueOrValues)) {
          for (const child of valueOrValues) {
            traverse(child, childAncestors, name);
          }
        } else if (valueOrValues) {
          traverse(valueOrValues, childAncestors, name);
        }
      }
    }
    if (node.placeholder) {
      traverse(node.placeholder, childAncestors, slotName);
    }
  };

  traverse(startNode, [], undefined);
};
